/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed product IDs */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} The key used to store the viewed product handles */
  static #STORAGE_KEY_HANDLES = 'viewedProductHandles';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * NOTE: For backward compatibility, productId is required, productHandle is optional.
   * @param {string} productId - The ID of the product to add.
   * @param {string} [productHandle] - The handle of the product to add (optional).
   */
  static addProduct(productId, productHandle) {
    // ---- IDs: keep existing behavior ----
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter(
      /** @type {string} */(id) => id !== productId
    );
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

    // ---- Handles: new parallel storage (optional) ----
    if (productHandle) {
      this.#addHandle(productHandle);
    }
  }

  /**
   * New helper to add a product HANDLE only (for new themes if you don't care about IDs).
   * @param {string} productHandle
   */
  static addProductHandle(productHandle) {
    this.#addHandle(productHandle);
  }

  /** @private */
  static #addHandle(productHandle) {
    try {
      const raw = localStorage.getItem(this.#STORAGE_KEY_HANDLES) || '[]';
      let handles = JSON.parse(raw);
      if (!Array.isArray(handles)) handles = [];

      handles = handles.filter((h) => h !== productHandle);
      handles.unshift(productHandle);
      handles = handles.slice(0, this.#MAX_PRODUCTS);

      localStorage.setItem(
        this.#STORAGE_KEY_HANDLES,
        JSON.stringify(handles)
      );
    } catch (e) {
      console.error('RecentlyViewed.addHandle error', e);
    }
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    localStorage.removeItem(this.#STORAGE_KEY_HANDLES);
  }

  /**
   * Retrieves the list of recently viewed product IDs.
   * @returns {string[]} The list of viewed product IDs.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }

  /**
   * Retrieves the list of recently viewed product HANDLES.
   * @returns {string[]} The list of viewed product handles.
   */
  static getProductHandles() {
    try {
      const raw = localStorage.getItem(this.#STORAGE_KEY_HANDLES) || '[]';
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map((h) => String(h));
    } catch (e) {
      console.error('RecentlyViewed.getProductHandles error', e);
      return [];
    }
  }
}
